import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  SlideFade,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";

import { parse } from "date-fns";
import { useState } from "react";
import { ImTicket } from "react-icons/im";
import { IoPlayCircleOutline } from "react-icons/io5";
import { MovieCardProps } from "../../@types/MovieCard";
import { Image } from "../../components";
import { SHOW_BOOKING_CLOSED } from "../../constant/alertMessage";
import {
  getShowStatusColorScheme,
  isAdvanceBooking,
} from "../../constant/enums";
import { useRouter } from "../../hooks/useRouter";
import ShowCaseTrailerModal from "../ShowCaseTrailerModal";
import GradeAWarn from "../WarningModules/gradeAWarn";

function MovieCard({
  title,
  poster,
  duration,
  videoLink,
  releaseDate,
  releaseDateFormat = "MMMM d yyyy",
  detailLink,
  grade,
  genre,
  handleDetailClick,
  isNowShowing = false,
  ctaButtonProps,
  movieShowTimes,
  showTimeList,
  showAudiName,
  imageProps,
  topGradeBoxProps,
  rollQualityBoxProps,
  topTagBoxProps,
  lowerTextContainerProps,
  imageBoxProps,
  showTimeButtonProps,
  showTimeListBoxProps,
  forceHideDivider = false,
  movieCardsTopContainerProps,
  movieCardTitleProps,
  durationProps,
  slideFadeContainerProps,
  toShowGenre = true,
  tag,
  showTag = true,
  roll_quality,
  showTrailerButton = true,
  trailerButtonSlideFadeProps,
  buyTicketButtonSlideFadeProps,
  toShowRunTime = true,
  durationValueProps,
  overlayOpenSx,
  overlayCloseSx,
  trailerButtonProps,
  trailerButtonLabel = "Trailer",
  showTrailerButtonIcon = false,
  trailerIconProps,
  showCtaButtonIcon = false,
  ctaIconProps,
  hideShowTimeTooltip = false,
  showRibbon = false,
  ribbonText,
  ribbonBoxProps,
  showBookingRibbon = true,
  bookingRibbonText = "Advance Booking",
  bookingRibbonBoxProps,
}: MovieCardProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modelroute, setModalRoute] = useState<string>("");
  const router = useRouter();
  const {
    isOpen: messageisOpen,
    onOpen: messageonOpen,
    onClose: messageonClose,
  } = useDisclosure();

  const actionButtonProps = {
    size: {
      base: "sm",
      sm: "lg",
    },
    variant: "outline",
    colorScheme: "primary",
    _hover: {
      background: "primary.500",
      color: "white",
      borderColor: "primary.500",
    },
    color: "white",
    borderColor: "white",
    px: "2.5rem",
    rounded: "full",
    fontWeight: 600,
    ...ctaButtonProps,
  };

  const parsedReleaseDate = releaseDate
    ? parse(releaseDate, releaseDateFormat, new Date())
    : null;
  const hasBookableShow = isNowShowing && isAdvanceBooking(parsedReleaseDate);

  const handlePrimaryAction = () => {
    if (grade === "A") {
      messageonOpen();
      setModalRoute(detailLink);
    } else if (handleDetailClick) {
      handleDetailClick();
    } else {
      router.push(detailLink);
    }
  };
  return (
    <Box
      overflow="visible"
      onMouseOver={onOpen}
      borderRadius="lg"
      itemScope
      itemType="http://schema.org/Movie"
      itemProp="movie"
      title={title}
      height={"full"}
      display="flex"
      flexDirection="column"
      onMouseLeave={onClose}
      onClick={handlePrimaryAction}
      cursor={"pointer"}
      position="relative"
      {...movieCardsTopContainerProps}
    >
      <Box
        {...imageBoxProps}
        position="relative"
        width="full"
        itemProp="image"
        itemScope
        title={title}
        role="link"
        aria-label={title}
        cursor="pointer"
      >
        <Image
          className="movie__card__image"
          src={poster}
          itemProp="image"
          alt={title}
          w={"150%"}
          {...imageProps}
          sx={{
            aspectRatio: "2/3",
            height: "auto",
            maxHeight: {
              base: "500px",
              xs: "390px",
            },
            objectFit: "cover",
            ...(imageProps?.sx || {}),
          }}
        />
        <Flex
          direction="column"
          gap={5}
          // No onTouchStart: on touch devices the overlay never opens (stays
          // opacity 0) so it can't get "stuck"; tapping the poster navigates via
          // the image Box onClick (clicks bubble up even through the overlay).
          {...slideFadeContainerProps}
          sx={{
            // Defaults that configs can override via slideFadeContainerProps.sx
            background: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(0.3rem)",
            _hover: { background: "rgba(0,0,0,0.7)" },
            opacity: isOpen ? 1 : 0,
            transition: "all 0.3s ease-in-out",
            alignItems: "center",
            justifyContent: "center",
            // State-dependent overrides: open or close sx from config.
            // These run before the always-on sx so the always-on transition
            // and transformOrigin survive in both states.
            ...(isOpen ? overlayOpenSx : overlayCloseSx),
            // Merge any tenant-provided overrides...
            ...slideFadeContainerProps?.sx,
            // ...but positioning must always win so the overlay sits ON TOP of
            // the poster, even when a config supplies its own sx (which would
            // otherwise clobber these and drop the overlay into normal flow).
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 1,
          }}
        >
          {showTrailerButton && (
            <SlideFade
              in={isOpen}
              delay={{
                enter: 0.3,
              }}
              offsetX="70px"
              {...trailerButtonSlideFadeProps}
            >
              {videoLink && (
                <ShowCaseTrailerModal
                  url={videoLink}
                  actionEl={
                    <Button
                      leftIcon={
                        showTrailerButtonIcon ? (
                          <IoPlayCircleOutline {...trailerIconProps} />
                        ) : (
                          <></>
                        )
                      }
                      {...actionButtonProps}
                      {...trailerButtonProps}
                    >
                      {trailerButtonLabel}
                    </Button>
                  }
                />
              )}
            </SlideFade>
          )}
          <SlideFade
            in={isOpen}
            delay={{
              enter: 0.3,
            }}
            offsetX="-70px"
            {...buyTicketButtonSlideFadeProps}
          >
            <Button
              leftIcon={
                showCtaButtonIcon ? (
                  <ImTicket {...(ctaIconProps as object)} />
                ) : undefined
              }
              {...actionButtonProps}
              onClick={handlePrimaryAction}
            >
              {isNowShowing ? "Buy Ticket" : "Details"}
            </Button>
          </SlideFade>
        </Flex>
        {grade && (
          <Button
            w={{
              base: 8,
              md: 10,
            }}
            h={{
              base: 8,
              md: 10,
            }}
            fontSize={{
              base: 10,
              md: 14,
            }}
            minW={0}
            p={0}
            color="white"
            aria-readonly
            background="primary.500"
            rounded="full"
            size="md"
            {...topGradeBoxProps}
            sx={{
              position: "absolute",
              top: {
                base: "0.5rem",
                md: "1rem",
              },
              right: {
                base: "0.5rem",
                md: "0.5rem",
              },
              ...(topGradeBoxProps?.sx || {}),
            }}
          >
            {grade}
          </Button>
        )}
        {roll_quality && roll_quality === "3D" && (
          <Button
            w={{
              base: 8,
              md: 10,
            }}
            h={{
              base: 8,
              md: 10,
            }}
            fontSize={{
              base: 10,
              md: 14,
            }}
            minW={0}
            p={0}
            color="white"
            aria-readonly
            background="primary.500"
            rounded="full"
            size="md"
            {...rollQualityBoxProps}
            sx={{
              position: "absolute",
              top: {
                base: "0.5rem",
                md: "1rem",
              },
              right: {
                base: "2rem",
                md: "3rem",
              },
              ...(rollQualityBoxProps?.sx || {}),
            }}
          >
            {roll_quality}
          </Button>
        )}
        {tag && showTag && (
          <Flex
            w="fit-content"
            color="white"
            justifyContent={"center"}
            alignItems={"center"}
            fontSize={{
              base: "xs",
              md: "xs",
            }}
            fontWeight={"bold"}
            zIndex={10}
            bg={"primary.500"}
            p={{
              base: "4px 6px",
              md: "4px 8px",
            }}
            {...topTagBoxProps}
            sx={{
              position: "absolute",
              top: {
                base: "0.5rem",
                md: "1rem",
              },
              left: "-10px",
              borderRadius: "0 8px 8px 0",
              boxSizing: "border-box",

              "&::before": {
                content: '""',
                position: "absolute",
                bottom: "-10px",
                left: "0",
                width: "0",
                height: "0",
                transform: "rotate(90deg)",
                borderTop: "10px solid",
                borderTopColor: "primary.700",
                borderRight: "10px solid transparent",
              },
              ...(topTagBoxProps?.sx || {}),
            }}
          >
            {tag}
          </Flex>
        )}
        {showBookingRibbon && hasBookableShow && (
          <Flex
            w="fit-content"
            color="white"
            justifyContent={"center"}
            alignItems={"center"}
            fontSize={{
              base: "xs",
              md: "xs",
            }}
            fontWeight={"bold"}
            bg={"primary.500"}
            p={{
              base: "4px 6px",
              md: "4px 8px",
            }}
            zIndex={10}
            {...bookingRibbonBoxProps}
            sx={{
              position: "absolute",
              bottom: -3,
              right: "-10px",
              borderRadius: "8px 0 0 8px",
              boxSizing: "border-box",

              "&::before": {
                content: '""',
                position: "absolute",
                top: "-10px",
                right: 0,
                width: "0",
                height: "0",
                borderStyle: "solid",
                borderWidth: "0 10px 10px 0",
                borderColor: "transparent",
                borderBottomColor: "primary.700",
              },
              ...(bookingRibbonBoxProps?.sx || {}),
            }}
          >
            {bookingRibbonText}
          </Flex>
        )}
        {showRibbon && ribbonText && (
          <Flex
            w="fit-content"
            color="white"
            justifyContent={"center"}
            alignItems={"center"}
            fontSize={{
              base: "xs",
              md: "xs",
            }}
            fontWeight={"bold"}
            bg={"primary.500"}
            p={{
              base: "4px 6px",
              md: "4px 8px",
            }}
            zIndex={10}
            {...ribbonBoxProps}
            sx={{
              position: "absolute",
              bottom: -3,
              right: "-10px",
              borderRadius: "8px 0 0 8px",
              boxSizing: "border-box",

              "&::before": {
                content: '""',
                position: "absolute",
                top: "-10px",
                right: 0,
                width: "0",
                height: "0",
                borderStyle: "solid",
                borderWidth: "0 10px 10px 0",
                borderColor: "transparent",
                borderBottomColor: "primary.700",
              },
              ...(ribbonBoxProps?.sx || {}),
            }}
          >
            {ribbonText}
          </Flex>
        )}
      </Box>
      <Box
        mt={{
          base: 2,
        }}
        flex="1"
        display="flex"
        flexDirection="column"
        {...lowerTextContainerProps}
        p={{
          xl: "10px",
          base: "5px",
        }}
      >
        <Heading
          mb={1}
          size={{
            base: "sm",
          }}
          color="surface.700"
          textTransform="capitalize"
          {...movieCardTitleProps}
        >
          {title}
        </Heading>
        {!isNowShowing && (
          <Text
            fontSize={{
              base: "xs",
            }}
            fontWeight={500}
            textTransform="capitalize"
            color="surface.300"
            {...durationProps}
          >
            Releasing on:{" "}
            <Text as="span" {...durationValueProps}>
              {releaseDate}
            </Text>
          </Text>
        )}
        {toShowGenre && (
          <Text
            fontSize={{
              base: "sm",
            }}
            fontWeight={500}
            textTransform="capitalize"
            color="surface.300"
            paddingBottom={{
              base: "10px",
              xs: "5px",
            }}
            {...durationProps}
          >
            {genre}
          </Text>
        )}
        {toShowRunTime && (
          <Text
            fontSize="sm"
            fontWeight={500}
            textTransform="capitalize"
            color="surface.300"
            paddingBottom={"10px"}
            {...durationProps}
          >
            {!isNowShowing && "Runtime: "} {duration}
          </Text>
        )}

        {isNowShowing && showTimeList && !forceHideDivider && <Divider />}

        {showTimeList && (
          <Flex wrap="wrap" gap="1rem" mt="0.7rem" {...showTimeListBoxProps}>
            {movieShowTimes?.map(
              (
                {
                  start_time,
                  available,
                  viewable,
                  showLink,
                  lock_web,
                  show_status,
                  auditorium,
                },
                index,
              ) => (
                <Tooltip
                  label={
                    lock_web && (viewable ?? available)
                      ? SHOW_BOOKING_CLOSED
                      : hideShowTimeTooltip
                        ? ""
                        : title
                  }
                  isDisabled={hideShowTimeTooltip && !(lock_web && (viewable ?? available))}
                  key={`show__time__tooltip__${index}`}
                >
                  <Button
                    key={`show__time__${index}`}
                    _hover={{ textDecoration: "none" }}
                    size="sm"
                    // Locked and already-started shows stay clickable: the
                    // seat layout opens read-only.
                    isDisabled={!(viewable ?? available)}
                    opacity={lock_web && (viewable ?? available) ? 0.75 : undefined}
                    colorScheme={getShowStatusColorScheme(show_status)}
                    onClick={() => {
                      if (grade === "A") {
                        messageonOpen();
                        setModalRoute(showLink);
                      } else {
                        router.push(showLink);
                      }
                    }}
                    h="auto"
                    py="0.35rem"
                    {...showTimeButtonProps}
                  >
                    <Flex direction="column" align="center" gap="0">
                      {showAudiName && auditorium?.name && (
                        <Text
                          as="span"
                          fontSize="xs"
                          fontWeight="normal"
                          opacity={0.8}
                          lineHeight="1.3"
                        >
                          {auditorium.name}
                        </Text>
                      )}
                      <Text as="span" lineHeight="1.4">
                        {start_time}
                      </Text>
                    </Flex>
                  </Button>
                </Tooltip>
              ),
            )}
          </Flex>
        )}
        <GradeAWarn
          isOpen={messageisOpen}
          onClose={messageonClose}
          onProceed={() => router.push(modelroute)}
          modalKey={"warning_module"}
        />
      </Box>
    </Box>
  );
}

export default MovieCard;
